// The town stream's content (phase D1): the townsfolk (`npcs`: the seams' item 3), the places their routines name
// (`npc-places`), the cutscenes (`cutscenes`: @rpg/camera timelines with their puppets), the marks the scenes stand
// actors on (`scene-marks`) and the crowd's animation LOD (`animation-lod`). Read once from the registry; the
// timelines compiled and checked against the names they use.
import { compileTimeline, validateTimeline, type CompiledTimeline, type TimelineJson } from "@rpg/camera";
import { validateRoutine, type RoutineBlock } from "@rpg/ai";
import type { AnimationLodOptions } from "@rpg/figure";
import type { SpikeContent } from "../content";
import type { Cutscene, Npc, NpcPlace, SceneMark } from "../content/types.gen";

/** One townsperson. */
export interface NpcDef {
  id: string;
  card: string;
  /** Its display name's string id. */
  name: string;
  home: string;
  affinity: boolean;
  colours: Record<string, string>;
  routine: RoutineBlock[];
  voice: Npc["voice"];
  greeting: string;
  walk: number;
}

/** A cutscene: its compiled timeline and its puppets. */
export interface SceneDef {
  id: string;
  json: TimelineJson;
  timeline: CompiledTimeline;
  puppets: { actor: string; card: string; mark: string }[];
}

export interface TownContent {
  npcs: NpcDef[];
  places: Map<string, NpcPlace>;
  marks: Map<string, SceneMark>;
  scenes: Map<string, SceneDef>;
  lod: AnimationLodOptions;
  /** The poses the scenes and activities may name. */
  poses: string[];
}

/** The speaker ids a line may name besides the townsfolk. */
export const SPEAKERS = ["narrator"] as const;

/** Read and check the town's content. Throws naming what is wrong (a timeline naming an unknown actor, mark, pose or
 *  line; a routine with overlapping blocks). */
export function readTownContent(c: SpikeContent): TownContent {
  const reg = c.registry;
  const npcs: NpcDef[] = reg.rows("npcs").map((n) => ({
    id: n.id,
    card: n.card.id,
    name: n.name.id,
    home: n.home,
    affinity: n.affinity === true,
    colours: Object.fromEntries(Object.entries(n.colours ?? {}).filter((e): e is [string, string] => typeof e[1] === "string")),
    routine: n.routine.map((b) => ({ from: b.from, to: b.to, activity: b.activity, place: b.place.id })),
    voice: n.voice,
    greeting: n.greeting.id,
    walk: n.walk,
  }));
  for (const n of npcs) {
    const bad = validateRoutine(n.routine);
    if (bad.length) throw new Error(`town: npc "${n.id}"'s routine: ${bad.join("; ")}`);
  }
  const places = new Map(reg.rows("npc-places").map((p) => [p.id, p]));
  const marks = new Map(reg.rows("scene-marks").map((m) => [m.id, m]));
  const poses = reg.rows("poses").map((p) => p.id);
  const lines = reg.rows("strings").map((s) => s.id);
  const scenes = new Map<string, SceneDef>();
  for (const row of reg.rows("cutscenes") as Cutscene[]) {
    const json = { id: row.id, steps: row.steps } as unknown as TimelineJson;
    const puppets = (row.puppets ?? []).map((p) => ({ actor: p.actor, card: p.card.id, mark: p.mark }));
    const bad = validateTimeline(json, { actors: ["hero", ...npcs.map((n) => n.id), ...puppets.map((p) => p.actor)], marks: [...marks.keys()], poses, lines });
    const speakers = new Set<string>([...SPEAKERS, ...npcs.map((n) => n.id)]);
    for (const s of row.steps) if (s.kind === "line" && !speakers.has(s.speaker)) bad.push(`${row.id}: unknown speaker "${s.speaker}"`);
    if (bad.length) throw new Error(`town: cutscene ${bad.join("; ")}`);
    scenes.set(row.id, { id: row.id, json, timeline: compileTimeline(json), puppets });
  }
  const l = reg.get("animation-lod", "townsfolk");
  if (!l) throw new Error('town: no animation-lod row "townsfolk"');
  const lod: AnimationLodOptions = { bands: l.bands.map((b) => ({ ...b })), offscreen: { every: l.offscreen }, ...(l.maxCatchUp !== undefined ? { maxCatchUp: l.maxCatchUp } : {}) };
  return { npcs, places, marks, scenes, lod, poses };
}
